import React from 'react';
import AIIntegrationSettingsForm from './AIIntegrationSettingsForm';
import ChargesSettingsForm from './ChargesSettingsForm';
import CurrencySettingsForm from './CurrencySettingsForm';
import DashboardSettingsForm from './DashboardSettingsForm';
import ExternalAPISettingsForm from './ExternalAPISettingsForm';
import FeedbackSettingsForm from './FeedbackSettingsForm';
import GUITableSettingsForm from './GUITableSettingsForm';
import GlobalBannerSettingsForm from './GlobalBannerSettingsForm';
import GoogleOAuthSettingsForm from './GoogleOAuthSettingsForm';
import LdapSettingsForm from './LdapSettingsForm';
import ServiceCreatorSettingsForm from './ServiceCreatorSettingsForm';
import SmtpSettingsForm from './SmtpSettingsForm';
import SubmissionBuilderSettingsForm from './SubmissionBuilderSettingsForm';

/**
 * Where a setting stands, for the settings that stand somewhere: a switch is
 * either on or off, and that is the question the page answers before any other.
 *
 * A setting with no switch has no such answer — it is a set of values, and
 * "Set" says nothing a reader can act on. Those carry no status at all.
 */
export type SettingStatus = 'on' | 'off';

/** the one key that means "this whole setting is switched on" */
const ENABLED = 'ENABLED';

export type SettingDescriptor = {
    /** GeneralSettings.name — the key the record is stored and fetched under */
    key: string;
    title: string;
    group: string;
    /** what this setting does, in the words of someone deciding whether to touch it */
    description: string;
    /** words worth finding it by that are not in its title */
    keywords?: string[];
    /**
     * True where the form carries an ENABLED switch governing the whole of the
     * setting — those show On or Off in the rail, before they are opened.
     *
     * Omit it where there is no such switch: a rate limit is not on or off, and
     * neither is a group of independent feature toggles, where saying "On"
     * because one of three is on would be a claim the setting does not make.
     * Lists and tables is the case to keep in mind — it has an ENABLED key, but
     * it switches the table refresh, not the setting, so it is not declared
     * here and shows no status.
     */
    hasEnabledSwitch?: boolean;
    Form: React.ComponentType<any>;
};

/**
 * Every setting this deployment has, in the groups they are thought about in.
 *
 * The order within a group is the order they are read in, and the group order
 * runs from what stops someone getting in at all, through what the platform
 * says and charges, to what it shows.
 */
export const SETTINGS: SettingDescriptor[] = [
    {
        key: 'LDAP',
        title: 'LDAP',
        group: 'Access',
        description:
            'Authentication against your directory server, and how its groups map onto team roles.',
        keywords: ['directory', 'active directory', 'sso', 'login', 'authentication', 'bind'],
        hasEnabledSwitch: true,
        Form: LdapSettingsForm,
    },
    {
        key: 'GoogleOAuth',
        title: 'Google sign-in',
        group: 'Access',
        description: 'Signing in with a Google account instead of a NetOrca password.',
        keywords: ['oauth', 'sso', 'login', 'authentication', 'client id', 'secret'],
        hasEnabledSwitch: true,
        Form: GoogleOAuthSettingsForm,
    },
    {
        key: 'SMTP',
        title: 'Email server',
        group: 'Notifications',
        description: 'The mail server notifications are sent through.',
        keywords: ['smtp', 'mail', 'email', 'host', 'port', 'tls'],
        hasEnabledSwitch: true,
        Form: SmtpSettingsForm,
    },
    {
        key: 'GlobalBanner',
        title: 'Global banner',
        group: 'Notifications',
        description: 'A message shown to everyone, above every page.',
        keywords: ['announcement', 'message', 'notice', 'maintenance'],
        hasEnabledSwitch: true,
        Form: GlobalBannerSettingsForm,
    },
    {
        key: 'Feedback',
        title: 'Feedback link',
        group: 'Notifications',
        description: 'A link where people can tell you what they think of NetOrca.',
        keywords: ['survey', 'support', 'contact'],
        hasEnabledSwitch: true,
        Form: FeedbackSettingsForm,
    },
    {
        key: 'ChargesSettings',
        title: 'Charges',
        group: 'Billing',
        description: 'Whether service charges are shown, and who is able to see them.',
        keywords: ['cost', 'price', 'billing', 'marketplace', 'money'],
        hasEnabledSwitch: true,
        Form: ChargesSettingsForm,
    },
    {
        key: 'Currency',
        title: 'Currency',
        group: 'Billing',
        description: 'The currency every charge is displayed in.',
        keywords: ['money', 'gbp', 'usd', 'eur', 'price'],
        hasEnabledSwitch: true,
        Form: CurrencySettingsForm,
    },
    {
        key: 'SubmissionBuilder',
        title: 'Submission builder',
        group: 'Features',
        description: 'The submission builder available to consumers in the interface.',
        keywords: ['declaration', 'wizard', 'submit', 'consumer'],
        Form: SubmissionBuilderSettingsForm,
    },
    {
        key: 'ServiceCreator',
        title: 'Service creation',
        group: 'Features',
        description: 'Authoring a service from the interface rather than through a submission.',
        keywords: ['author', 'schema', 'create service', 'service owner'],
        Form: ServiceCreatorSettingsForm,
    },
    {
        key: 'AIIntegration',
        title: 'AI integration',
        group: 'Features',
        description: 'The AI features on offer, and the prompts the universal executor runs on.',
        keywords: ['pack', 'llm', 'prompt', 'validator', 'processor', 'universal executor'],
        Form: AIIntegrationSettingsForm,
    },
    {
        key: 'GUITableSettings',
        title: 'Lists and tables',
        group: 'Interface',
        description: 'How lists refresh themselves, and the filters they open with.',
        keywords: ['refresh', 'polling', 'filter', 'default', 'grid'],
        Form: GUITableSettingsForm,
    },
    {
        key: 'DashboardSettings',
        title: 'Dashboard',
        group: 'Interface',
        description: 'How the dashboard calculates the statistics it graphs.',
        keywords: ['graph', 'chart', 'rolling average', 'window', 'statistics'],
        Form: DashboardSettingsForm,
    },
    {
        key: 'ExternalAPISettings',
        title: 'Outbound rate limits',
        group: 'Limits',
        description:
            'The following settings help control the frequency of outbound webhook and healthcheck requests:',
        keywords: ['webhook', 'healthcheck', 'throttle', 'rate limit', 'requests'],
        Form: ExternalAPISettingsForm,
    },
];

/** the groups, in the order the rail lists them */
export const SETTING_GROUPS: string[] = SETTINGS.reduce<string[]>(
    (groups, setting) => (groups.includes(setting.group) ? groups : groups.concat(setting.group)),
    [],
);

/**
 * On or Off for the settings declared above as having a switch, null for the
 * rest — nothing is drawn for those. Declared rather than sniffed out of the
 * stored config, because an ENABLED key is not proof the switch governs the
 * whole setting: Lists and tables keeps one for its refresh alone. A switch
 * with no record saved is Off, which is what its form will show too.
 */
export const statusOf = (setting: SettingDescriptor, record: any): SettingStatus | null => {
    if (!setting.hasEnabledSwitch) return null;
    return record?.config?.[ENABLED] === true ? 'on' : 'off';
};

export const STATUS_LABELS: Record<SettingStatus, string> = {
    on: 'On',
    off: 'Off',
};

/** does this setting answer to what was typed in the search box? */
export const matches = (setting: SettingDescriptor, term: string) => {
    if (!term) return true;
    const needle = term.trim().toLowerCase();
    return (
        setting.title.toLowerCase().includes(needle)
        || setting.key.toLowerCase().includes(needle)
        || setting.group.toLowerCase().includes(needle)
        || setting.description.toLowerCase().includes(needle)
        || (setting.keywords || []).some((word) => word.includes(needle))
    );
};
